import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { getAuthToken } from '../../utils/apiClient';

const API_BASE = process.env.REACT_APP_API_URL
  ? (process.env.REACT_APP_API_URL.endsWith('/api') ? process.env.REACT_APP_API_URL : `${process.env.REACT_APP_API_URL.replace(/\/?$/, '')}/api`)
  : '/api';

function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:1px 4px;border-radius:3px;font-size:0.82em">$1</code>');
  const lines = html.split('\n');
  const result = [];
  let inList = false;
  let listType = null;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const numberedMatch = line.match(/^(\d+)\.\s+(.*)/);
    const bulletMatch = line.match(/^\s*[-•]\s+(.*)/);
    if (numberedMatch) {
      if (!inList || listType !== 'ol') {
        if (inList) result.push(listType === 'ol' ? '</ol>' : '</ul>');
        result.push('<ol style="margin:4px 0;padding-left:22px">');
        inList = true; listType = 'ol';
      }
      result.push(`<li style="margin-bottom:2px">${numberedMatch[2]}</li>`);
    } else if (bulletMatch) {
      if (!inList || listType !== 'ul') {
        if (inList) result.push(listType === 'ol' ? '</ol>' : '</ul>');
        result.push('<ul style="margin:4px 0;padding-left:22px">');
        inList = true; listType = 'ul';
      }
      result.push(`<li style="margin-bottom:2px">${bulletMatch[1]}</li>`);
    } else {
      if (inList) {
        result.push(listType === 'ol' ? '</ol>' : '</ul>');
        inList = false; listType = null;
      }
      result.push(line === '' ? '<br/>' : line + (i < lines.length - 1 ? '<br/>' : ''));
    }
  }
  if (inList) result.push(listType === 'ol' ? '</ol>' : '</ul>');
  return result.join('');
}

const DEFAULT_WELCOME = 'Hello! I\'m your LGL Tools Help Assistant. How can I help you use the portal today?';
const PERMISSION_WELCOME = 'Hello! I\'m your LGL Tools Help Assistant. Ask how to use the portal, or whether a person has access to a feature and how to grant it.';

const HelpbotWidget = () => {
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [canAskPermissions, setCanAskPermissions] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: DEFAULT_WELCOME }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [feedbackMap, setFeedbackMap] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;
    const checkAccess = async () => {
      try {
        const token = await getAuthToken(instance, accounts);
        const res = await fetch(`${API_BASE}/helpbot/access`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (!cancelled) {
          setHasAccess(data.hasAccess === true);
          const ask = data.canAskPermissions === true;
          setCanAskPermissions(ask);
          if (ask) {
            setMessages((prev) => {
              if (prev.length === 1 && prev[0].role === 'assistant' && prev[0].content === DEFAULT_WELCOME) {
                return [{ role: 'assistant', content: PERMISSION_WELCOME }];
              }
              return prev;
            });
          }
        }
      } catch {
        if (!cancelled) setHasAccess(false);
      }
    };
    checkAccess();
    return () => { cancelled = true; };
  }, [instance, accounts]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const token = await getAuthToken(instance, accounts);
      const response = await fetch(`${API_BASE}/helpbot/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: newMessages.filter(m => m.role !== 'system'),
          pagePath: location.pathname,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Request failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';

      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') break;

          try {
            const parsed = JSON.parse(payload);
            if (parsed.content) {
              assistantContent += parsed.content;
              const captured = assistantContent;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: captured };
                return updated;
              });
            }
          } catch { /* skip malformed chunks */ }
        }
      }
    } catch (err) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: `Sorry, I encountered an error: ${err.message}. Please try again.` }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitFeedback = async (msgIndex, rating) => {
    if (feedbackMap[msgIndex]) return;
    setFeedbackMap(prev => ({ ...prev, [msgIndex]: rating }));

    const assistantMsg = messages[msgIndex];
    const userMsg = messages.slice(0, msgIndex).reverse().find(m => m.role === 'user');

    try {
      const token = await getAuthToken(instance, accounts);
      await fetch(`${API_BASE}/helpbot/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          rating,
          messageContent: userMsg?.content || '',
          responseContent: assistantMsg?.content || '',
        }),
      });
    } catch { /* silent fail for feedback */ }
  };

  if (hasAccess !== true) return null;

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          aria-label="Open Help Chat"
          style={styles.fab}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div style={styles.panel}>
          {/* Header */}
          <div style={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Help Assistant</span>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() => setMessages([{ role: 'assistant', content: canAskPermissions ? PERMISSION_WELCOME : DEFAULT_WELCOME }])}
                title="Clear chat"
                style={styles.headerBtn}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                title="Close"
                style={styles.headerBtn}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={styles.messagesContainer}>
            {messages.map((msg, i) => (
              <div key={i} style={{ marginBottom: '10px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{
                    ...styles.bubble,
                    ...(msg.role === 'user' ? styles.userBubble : styles.assistantBubble),
                  }}>
                    {msg.role === 'assistant' && msg.content
                      ? <span dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                      : (msg.content || (isLoading && i === messages.length - 1 ? '...' : ''))
                    }
                  </div>
                </div>
                {msg.role === 'assistant' && i > 0 && msg.content && !(isLoading && i === messages.length - 1) && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px', paddingLeft: '4px' }}>
                    <button
                      onClick={() => submitFeedback(i, 'helpful')}
                      disabled={!!feedbackMap[i]}
                      title="Helpful"
                      style={{
                        ...styles.feedbackBtn,
                        color: feedbackMap[i] === 'helpful' ? '#16a34a' : '#9ca3af',
                        opacity: feedbackMap[i] && feedbackMap[i] !== 'helpful' ? 0.3 : 1,
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill={feedbackMap[i] === 'helpful' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                      </svg>
                    </button>
                    <button
                      onClick={() => submitFeedback(i, 'not_helpful')}
                      disabled={!!feedbackMap[i]}
                      title="Not helpful"
                      style={{
                        ...styles.feedbackBtn,
                        color: feedbackMap[i] === 'not_helpful' ? '#dc2626' : '#9ca3af',
                        opacity: feedbackMap[i] && feedbackMap[i] !== 'not_helpful' ? 0.3 : 1,
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill={feedbackMap[i] === 'not_helpful' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                      </svg>
                    </button>
                    {feedbackMap[i] && (
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af', alignSelf: 'center', marginLeft: '4px' }}>
                        Thanks for the feedback!
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={styles.inputArea}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={canAskPermissions ? 'Ask about a feature, or who has access…' : 'Ask a question...'}
              disabled={isLoading}
              style={styles.input}
            />
            <button
              onClick={sendMessage}
              disabled={isLoading || !input.trim()}
              style={{
                ...styles.sendBtn,
                opacity: (isLoading || !input.trim()) ? 0.5 : 1,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  fab: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    boxShadow: '0 4px 16px rgba(220, 53, 69, 0.4)',
    cursor: 'pointer',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  panel: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '380px',
    height: '520px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.22)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid #2a2a2a',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(220, 53, 69, 0.85) 100%)',
    color: 'white',
    flexShrink: 0,
  },
  headerBtn: {
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    opacity: 0.85,
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    backgroundColor: '#f5f7fa',
  },
  bubble: {
    maxWidth: '85%',
    padding: '10px 14px',
    borderRadius: '12px',
    fontSize: '0.875rem',
    lineHeight: '1.45',
    wordBreak: 'break-word',
  },
  userBubble: {
    backgroundColor: '#dc3545',
    color: 'white',
    borderBottomRightRadius: '4px',
    whiteSpace: 'pre-wrap',
  },
  assistantBubble: {
    backgroundColor: '#fff',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderBottomLeftRadius: '4px',
  },
  inputArea: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #d0d5dd',
    borderRadius: '8px',
    fontSize: '0.875rem',
    outline: 'none',
    backgroundColor: '#f8f9fa',
  },
  sendBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  feedbackBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.15s',
  },
};

export default HelpbotWidget;
